import { Maybe } from 'graphql/jsutils/Maybe'
import { GetServerSideProps, NextPage } from 'next'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { Seo, VideoJsonLd } from '@/layout/Seo'
import { useDataForVideo } from '@/layout/Seo/SeoHelper'
import { GetAppBannerProvider } from '@/organisms/GetAppBanner'
import { getServerSideClient } from '@/services/ApolloClient/server/ServerApolloClient'
import { getPageByPath } from '@/services/CmsService/Pages'
import { createVideoDeepLink } from '@/services/DeepLinkService'
import { ShopifyStoreConfig } from '@/services/EarlyAccessService'
import { getContentfulProjectTheme } from '@/services/ProjectThemeService'
import {
  getProjectMeta,
  getVideoMetaData,
  GetVideoSeoMetaResponse,
  getWatchProjectEarlyAccess,
  Project,
  ProjectMeta,
  VideoMetaData,
} from '@/services/ProjectsService'
import { fetchPageDataContext, PageDataContext, PageModel } from '@/services/RenderService'
import { getShopifyConfigurationForProject } from '@/services/ShopifyConfigurationService'
import { ProjectTheme } from '@/types/codegen-contentful'
import { getLocaleFromParams } from '@/utils/LocaleUtil'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { shouldHideFromSearchEngines } from '@/utils/seo/searchEngineHelper'
import { loadTranslations } from '@/utils/translate/translate-server'
import { getUserGuildStatus, getUserUuidFromJwt } from '@/utils/users/usersServer'
import { WatchVideoView } from '@/views/WatchVideoView'

export const WatchVideoPage: NextPage<
  GetVideoSeoMetaResponse & {
    deepLink: string
    locale: string
    path: string
    projectMeta: ProjectMeta
    shopifyStore?: ShopifyStoreConfig

    projectData: Project
    pageDataContext: Maybe<PageDataContext>
    page: Maybe<PageModel>
    contentfulProjectTheme: Maybe<ProjectTheme>
    isGuildMember?: boolean
  }
> = (props) => {
  const video = props.video as VideoMetaData
  const { meta, jsonLd } = useDataForVideo(
    props.locale,
    props.logoCloudinaryPath,
    props.projectName,
    props.projectSlug,
    video,
  )
  const shouldHide = shouldHideFromSearchEngines(props.projectMeta)

  return (
    <>
      <Seo {...meta} nofollow={shouldHide} noindex={shouldHide} path={props.path} />
      <VideoJsonLd {...jsonLd} />
      <ProjectMetaProvider project={props.projectMeta}>
        <GetAppBannerProvider deepLink={props.deepLink} projectSlug={props.projectSlug} context="video">
          <WatchVideoView
            page={props.page}
            pageDataContext={props.pageDataContext}
            contentfulProjectTheme={props.contentfulProjectTheme}
            projectData={props.projectData}
            isGuildMember={props.isGuildMember}
            projectName={props.projectName}
            projectSlug={props.projectSlug}
            video={video}
            shopifyStore={props.shopifyStore}
          />
        </GetAppBannerProvider>
      </ProjectMetaProvider>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ preview = false, req, res, params = {}, resolvedUrl }) =>
  getServerSidePropsErrorHandler({ resolvedUrl }, async () => {
    const locale = getLocaleFromParams(params)
    const guid = params.guid as string
    const page = params.page as string
    const projectSlug = params.project as string
    let result
    let projectMeta: ProjectMeta | undefined

    if (projectSlug === slugs.theChosen) {
      return {
        redirect: {
          destination: paths.watch.index,
          permanent: false,
        },
      }
    }

    const shopifyStore = await getShopifyConfigurationForProject(projectSlug, { locale, preview })
    const client = getServerSideClient({ req, res })

    try {
      result = await getVideoMetaData({ guid, projectSlug }, client)
      projectMeta = await getProjectMeta({ slug: projectSlug }, client, { locale, preview: false })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`Error getting Video guid=${guid}`, e)
      return {
        redirect: {
          destination: `${paths.watch.index}/${params.project}`,
          permanent: false,
        },
      }
    }

    if (!projectMeta) {
      return {
        redirect: {
          destination: paths.watch.index,
          permanent: false,
        },
      }
    }

    const path = `${paths.watch.index}/${projectSlug}/videos/${page}/${guid}`

    const deepLink = await createVideoDeepLink(
      {
        path,
        guid,
        projectSlug,
        projectName: result?.projectName,
        contentName: result?.video?.title as string,
        audioLocale: locale,
        captionLocale: locale,
      },
      {
        campaign: 'prompt-app-install',
        feature: 'app-prompt',
        channel: 'angel-web',
      },
    )

    const uuid = getUserUuidFromJwt(req, res) ?? null
    const guildStatus = !uuid ? false : await getUserGuildStatus(uuid)
    const cmsPage = await getPageByPath(`${paths.watch.index}/${params.project}/videos/${page}/${guid}`, {
      preview,
      locale,
    })
    const pageDataContext = cmsPage ? await fetchPageDataContext(cmsPage, preview, locale) : null
    const contentfulProjectTheme = await getContentfulProjectTheme(projectSlug, preview)
    const projectData = await getWatchProjectEarlyAccess(projectSlug, projectMeta, client)

    return {
      notFound: !result.video,
      props: {
        deepLink,
        locale,
        logoCloudinaryPath: result.logoCloudinaryPath,
        path,
        projectMeta,
        projectName: result.projectName,
        projectSlug,
        video: result.video,
        shopifyStore,
        page: cmsPage,
        pageDataContext,
        contentfulProjectTheme,
        projectData,
        isGuildMember: Boolean(guildStatus && guildStatus?.isGuildMember),
        ...(await loadTranslations(locale, ['common', 'home', 'watch', 'account', 'ads'])),
      },
    }
  })

export default WatchVideoPage
